/**
 * Server-side question registry.
 *
 * The question bank lives in `@qd/questions` and includes correct-answer keys.
 * The reducer must keep `correctIndex` server-side: clients only ever see
 * `ClientSafeQuestion` (no key). This module owns the projection.
 *
 * Tier 2 and Tier 3 banks may be empty in the current Phase-6 round 1; in
 * that case we fall back to Tier 1 rather than throw, so the reducer stays
 * playable end-to-end. Replays remain deterministic because the fallback is
 * a pure function of `(tier, draw deck order)`.
 */

import type { ClientSafeQuestion, QuestionId } from '@qd/protocol';
import {
  allQuestions,
  questionsForTier,
  type Question,
} from '@qd/questions';

/** Stable lookup: id → full Question with correctIndex. */
const QUESTION_INDEX: ReadonlyMap<QuestionId, Question> = new Map(
  allQuestions.map((q) => [q.id, q] as const),
);

export function lookupQuestion(id: QuestionId): Question | undefined {
  return QUESTION_INDEX.get(id);
}

/** Return the full sorted set of question IDs for a tier (with fallback). */
export function questionIdsForTier(tier: 1 | 2 | 3): readonly QuestionId[] {
  const direct = questionsForTier(tier);
  if (direct.length > 0) return direct.map((q) => q.id);
  // Fallback: walk down to tier 1, which is guaranteed populated.
  return questionsForTier(1).map((q) => q.id);
}

/** Strip server-only fields for client transmission. */
export function toClientSafeQuestion(q: Question): ClientSafeQuestion {
  // Map @qd/questions kind names to the wire-protocol kind names.
  let kind: 'mcq' | 'tf' | 'scenario';
  switch (q.kind) {
    case 'multiple-choice':
      kind = 'mcq';
      break;
    case 'true-false':
      kind = 'tf';
      break;
    case 'scenario':
      kind = 'scenario';
      break;
  }

  // Build the object conditionally so we don't include `choices: undefined`
  // (incompatible with `exactOptionalPropertyTypes`).
  const base = {
    id: q.id,
    tier: q.tier,
    documentType: q.documentType,
    kind,
    difficulty: q.difficulty,
    prompt: q.prompt,
    isoClauseRef: q.isoClauseRef,
  } as const;
  return q.choices ? { ...base, choices: q.choices } : base;
}

/**
 * Compare a player's submitted answer to the canonical correctIndex.
 *
 * Numeric for MCQ/scenario (index of correct choice).
 * Boolean for true-false.
 * Returns `true` if correct.
 */
export function resolveAnswer(
  question: Question,
  submitted: number | boolean,
): boolean {
  if (typeof question.correctIndex === 'boolean') {
    return submitted === question.correctIndex;
  }
  return submitted === question.correctIndex;
}
